import { computed, onScopeDispose, ref, watch } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import { addDays } from '@/domain/reservations/reservation-time';
import type {
  IReservationDateOverride,
  IReservationSettingsConfiguration,
} from '@/interfaces/IReservationSettings';

/** Each direction owns its request cursor, so opening history cannot cancel a future page. */
export function useReservationDateOverrides(
  configuration: () => {
    settings: Pick<
      IReservationSettingsConfiguration['settings'],
      'restaurantId' | 'branchId' | 'maxAdvanceDays'
    >;
    overrides: IReservationDateOverride[];
  },
  today: () => string
) {
  const upcoming = ref<IReservationDateOverride[]>([]);
  const past = ref<IReservationDateOverride[]>([]);
  const newestFetched = ref('');
  const oldestFetched = ref<string | null>(null);
  const pastOpen = ref(false);
  const loadingFuture = ref(false);
  const loadingPast = ref(false);
  const futureError = ref(false);
  const pastError = ref(false);
  let futureRequest = 0;
  let pastRequest = 0;
  let futureRetry: { from: string; to: string } | null = null;
  let pastRetry: { from: string; to: string } | null = null;
  let alive = true;
  const scope = computed(() => {
    const { restaurantId, branchId } = configuration().settings;
    return `${restaurantId}:${branchId}:${today()}`;
  });
  onScopeDispose(() => {
    alive = false;
    futureRequest++;
    pastRequest++;
  });
  function merge(
    current: IReservationDateOverride[],
    rows: IReservationDateOverride[],
    from: string,
    to: string,
    descending = false
  ) {
    return [
      ...current.filter((row) => row.date < from || row.date > to),
      ...rows,
    ].sort((a, b) =>
      descending ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    );
  }
  async function loadFuture(until?: string, reset = false) {
    if (loadingFuture.value && !reset && !until) return;
    const seq = ++futureRequest;
    const identity = scope.value;
    const { restaurantId, branchId, maxAdvanceDays } = configuration().settings;
    const retry = !until && !reset ? futureRetry : null;
    const from =
      retry?.from ??
      (reset || !newestFetched.value
        ? today()
        : addDays(newestFetched.value, 1));
    const to =
      retry?.to ??
      until ??
      (reset
        ? newestFetched.value || addDays(today(), Math.max(365, maxAdvanceDays))
        : addDays(from, 364));
    loadingFuture.value = true;
    futureError.value = false;
    try {
      const rows = await ReservationsService.getOverrides(
        restaurantId,
        branchId,
        from,
        to
      );
      if (!alive || identity !== scope.value || seq !== futureRequest) return;
      upcoming.value = merge(upcoming.value, rows, from, to);
      newestFetched.value = to;
      futureRetry = null;
      loadingFuture.value = false;
    } catch {
      if (!alive || identity !== scope.value || seq !== futureRequest) return;
      loadingFuture.value = false;
      futureError.value = true;
      futureRetry = { from, to };
    }
  }
  async function loadPast(reset = false, since?: string) {
    if (loadingPast.value && !reset && !since) return;
    const seq = ++pastRequest;
    const identity = scope.value;
    const { restaurantId, branchId } = configuration().settings;
    const retry = !since && !reset ? pastRetry : null;
    const to =
      retry?.to ??
      addDays(reset ? today() : oldestFetched.value ?? today(), -1);
    const from =
      retry?.from ??
      since ??
      (reset && oldestFetched.value ? oldestFetched.value : addDays(to, -364));
    loadingPast.value = true;
    pastError.value = false;
    try {
      const rows = await ReservationsService.getOverrides(
        restaurantId,
        branchId,
        from,
        to
      );
      if (!alive || identity !== scope.value || seq !== pastRequest) return;
      past.value = merge(past.value, rows, from, to, true);
      oldestFetched.value = from;
      pastRetry = null;
      loadingPast.value = false;
    } catch {
      if (!alive || identity !== scope.value || seq !== pastRequest) return;
      loadingPast.value = false;
      pastError.value = true;
      pastRetry = { from, to };
    }
  }
  function togglePast() {
    pastOpen.value = !pastOpen.value;
    if (pastOpen.value && !oldestFetched.value) void loadPast();
  }
  function saved(row: IReservationDateOverride) {
    const { restaurantId, branchId } = configuration().settings;
    if (
      !alive ||
      row.restaurantId !== restaurantId ||
      row.branchId !== branchId
    )
      return;
    if (row.date >= today()) {
      upcoming.value = merge(upcoming.value, [row], row.date, row.date);
      const through =
        newestFetched.value ||
        addDays(
          today(),
          Math.max(365, configuration().settings.maxAdvanceDays)
        );
      void loadFuture(row.date > through ? row.date : through, true);
    } else {
      pastOpen.value = true;
      past.value = merge(past.value, [row], row.date, row.date, true);
      void loadPast(
        true,
        !oldestFetched.value || row.date < oldestFetched.value
          ? row.date
          : undefined
      );
    }
  }
  function removed(row: IReservationDateOverride) {
    upcoming.value = upcoming.value.filter((item) => item.id !== row.id);
    past.value = past.value.filter((item) => item.id !== row.id);
    if (row.date >= today()) void loadFuture(undefined, true);
    else if (pastOpen.value) void loadPast(true);
  }
  watch(
    scope,
    () => {
      futureRequest++;
      pastRequest++;
      futureRetry = null;
      pastRetry = null;
      upcoming.value = configuration().overrides.filter(
        (row) => row.date >= today()
      );
      past.value = [];
      newestFetched.value = '';
      oldestFetched.value = null;
      loadingPast.value = false;
      pastError.value = false;
      pastOpen.value = false;
      void loadFuture(undefined, true);
    },
    { immediate: true, flush: 'sync' }
  );
  return {
    upcoming,
    past,
    newestFetched,
    oldestFetched,
    pastOpen,
    loadingFuture,
    loadingPast,
    futureError,
    pastError,
    loadFuture,
    loadPast,
    togglePast,
    saved,
    removed,
  };
}
